import type { AppRole, AppUser } from './session';

/**
 * 로컬 개발 전용 셸 미리보기 모드.
 *
 * 왜 필요한가:
 *   부팅은 세션 확인 → cad_app_user 조회 순으로 진행된다.
 *   따라서 Supabase 에 마이그레이션이 적용되기 전이거나 계정이 준비되지 않은 상태에서는
 *   화면(셸 · 라우팅 · 패널)을 전혀 볼 수 없어 UI 작업이 막힌다.
 *   이 모드는 그 교착을 푸는 용도이며, **localhost 에서만** 동작한다.
 *
 * 사용:
 *   http://localhost:5273/#/dashboard?devshell=1
 *   http://localhost:5273/#/admin?devshell=cad_admin      ← 역할 지정
 *
 * 안전장치:
 *   · localhost / 127.0.0.1 / [::1] 이외에서는 무조건 null 을 반환한다.
 *   · 실제 Supabase 세션을 만들지 않는다. DB 접근은 여전히 RLS 로 차단된다.
 *   · 화면 상단에 경고 배너가 표시되어 실데이터로 오인할 수 없다.
 */

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

const VALID_ROLES: AppRole[] = [
  'admin',
  'cad_admin',
  'designer',
  'reviewer',
  'store',
  'vendor',
  'auditor',
  'user',
];

export function isLocalhost(): boolean {
  return LOCAL_HOSTS.has(window.location.hostname);
}

/** 미리보기 모드 여부. 저장소 계층이 고정 데이터로 전환할지 판단하는 데 쓴다. */
export function isDevPreview(): boolean {
  return devPreviewUser() !== null;
}

/** devshell 파라미터가 있으면 가짜 사용자를 반환한다. 그 외에는 null. */
export function devPreviewUser(): AppUser | null {
  if (!isLocalhost()) return null;

  // 해시 뒤(#/admin?devshell=...)와 일반 쿼리(?devshell=...) 양쪽을 모두 인식한다.
  const fromSearch = new URLSearchParams(window.location.search).get('devshell');
  const hashQuery = window.location.hash.split('?')[1] ?? '';
  const fromHash = new URLSearchParams(hashQuery).get('devshell');
  const raw = fromSearch ?? fromHash;
  if (raw === null) return null;

  const role = (VALID_ROLES as string[]).includes(raw) ? (raw as AppRole) : 'admin';

  return {
    email: 'dev@localhost',
    name: '개발 미리보기',
    role,
    dept: 'DX전략부',
    store_code: null,
    active: true,
  };
}
